import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import type {PersonnelStackParamList} from '@/app/routes';
import {
  DETACHED_RETENTION_DAYS,
  fetchDetachedPersonnel,
  purgeDetachedPersonnel,
  returnDetachedPersonnel,
  type DetachedPerson,
} from '@/entities/personnel/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {formatIsoDate} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {AppInput} from '@/shared/ui/input';
import {Page} from '@/shared/ui/page';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';

/**
 * «Сотрудники» → откреплённые (разбор 2026-10-08, B3/B4).
 *
 * Откреплённый не проходит в дверь и лица у него нет, но личность хранится: его
 * можно вернуть в штат или удалить насовсем. Решения нет год — удаляется сам; дату
 * показываем, чтобы это не стало сюрпризом.
 */
type Props = NativeStackScreenProps<PersonnelStackParamList, 'PersonnelDetached'>;

const styles = StyleSheet.create({
  list: {gap: spacing(2)},
  row: {gap: spacing(1)},
  buttons: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2), marginTop: spacing(2)},
});

const PAGE = 50;

/** Дата окончательного удаления — открепление плюс срок хранения. */
function purgeDate(detachedAt: string): string | null {
  const ms = Date.parse(detachedAt);
  if (Number.isNaN(ms)) {
    return null;
  }
  return formatIsoDate(new Date(ms + DETACHED_RETENTION_DAYS * 86_400_000).toISOString());
}

function DetachedRow({companyId, person}: {companyId: string; person: DetachedPerson}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  // Удаление насовсем — в два нажатия, как открепление в карточке: промах не
  // должен стирать человека безвозвратно.
  const [armed, setArmed] = useState(false);

  const done = async () => {
    await qc.invalidateQueries({queryKey: ['personnel-detached', companyId]});
    await qc.invalidateQueries({queryKey: ['personnel-list', companyId]});
  };
  const back = useMutation({
    mutationFn: () => returnDetachedPersonnel(companyId, person.record_id),
    onSuccess: done,
  });
  const purge = useMutation({
    mutationFn: () => purgeDetachedPersonnel(companyId, person.record_id),
    onSuccess: done,
  });

  const detached = formatIsoDate(person.detached_at);
  const until = purgeDate(person.detached_at);
  const error = (back.error ?? purge.error) as Error | null;

  return (
    <Card padding={3} testID={`detached-${person.record_id}`}>
      <View style={styles.row}>
        <AppText variant="subtitle" numberOfLines={1}>
          {person.full_name}
        </AppText>
        <AppText variant="caption" numberOfLines={1}>
          {[person.employee_number, person.position].filter(Boolean).join(' · ') ||
            t('reports.noPosition')}
        </AppText>
        {detached ? (
          <AppText variant="micro" color={p.mutedForeground}>
            {t('reports.detachedOn', {date: detached})}
            {until ? ` · ${t('detached.purgeOn', {date: until})}` : ''}
          </AppText>
        ) : null}
      </View>
      {error ? <Banner tone="error" title={error.message} /> : null}
      <View style={styles.buttons}>
        <AppButton
          title={t('detached.return')}
          icon="refresh"
          tone="outline"
          size="md"
          onPress={() => back.mutate()}
          loading={back.isPending}
          disabled={purge.isPending}
          testID={`detached-return-${person.record_id}`}
        />
        {armed ? (
          <AppButton
            title={t('detached.confirmPurge')}
            icon="trash"
            tone="destructive"
            size="md"
            onPress={() => purge.mutate()}
            loading={purge.isPending}
            disabled={back.isPending}
            testID={`detached-purge-confirm-${person.record_id}`}
          />
        ) : (
          <AppButton
            title={t('detached.purge')}
            tone="ghost"
            size="md"
            onPress={() => setArmed(true)}
            disabled={back.isPending}
            testID={`detached-purge-${person.record_id}`}
          />
        )}
      </View>
    </Card>
  );
}

export function DetachedPersonnelScreen(_: Props) {
  const {t} = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ['personnel-detached', companyId, search.trim(), page],
    queryFn: () => fetchDetachedPersonnel(companyId as string, search.trim(), page, PAGE),
    enabled: !!companyId,
  });

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.staff" />;
  }

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <Page scroll width="form" title={t('detached.title')} testID="staff-detached">
      <AppText variant="body">
        {t('detached.hint', {days: DETACHED_RETENTION_DAYS})}
      </AppText>
      <AppInput
        icon="search"
        value={search}
        onChangeText={v => {
          setSearch(v);
          setPage(1);
        }}
        placeholder={t('detached.search')}
        testID="detached-search"
      />
      {list.isError ? (
        <Banner tone="error" title={(list.error as Error).message} />
      ) : list.isPending ? (
        <View style={styles.list}>
          <Skeleton height={72} />
          <Skeleton height={72} />
        </View>
      ) : items.length === 0 ? (
        <EmptyState icon="people" title={t('detached.empty')} />
      ) : (
        <View style={styles.list}>
          {items.map(person => (
            <DetachedRow key={person.record_id} companyId={companyId} person={person} />
          ))}
        </View>
      )}
      {pages > 1 ? (
        <View style={styles.buttons}>
          <AppButton
            title={t('detached.prev')}
            tone="outline"
            size="md"
            disabled={page <= 1}
            onPress={() => setPage(n => Math.max(1, n - 1))}
          />
          <AppText variant="caption">{`${page} / ${pages}`}</AppText>
          <AppButton
            title={t('detached.next')}
            tone="outline"
            size="md"
            disabled={page >= pages}
            onPress={() => setPage(n => Math.min(pages, n + 1))}
          />
        </View>
      ) : null}
    </Page>
  );
}
